import { ChildService, HotelFeature, type HotelProduct } from '@/entities/hotel';
import { isTravelataOperatorId } from '@/entities/operator';
import { type Product } from '@/entities/product';
import { getProductPrepaymentsFilterKeys } from './prepaymentsFilter';
import { FilterGroupId, type FiltersModel, type RatingFilterKey, type SerpProduct } from '../model/types';

const RATING_PRECISION = 10;

export const RATING_FILTER_THRESHOLDS: Readonly<Record<RatingFilterKey, number>> = {
  best: 4.5,
  better: 4,
  good: 3.5,
};

export const AND_FILTER_GROUP_IDS: ReadonlySet<FilterGroupId> = new Set([
  FilterGroupId.Facilities,
  FilterGroupId.WaterActivities,
  FilterGroupId.ChildServices,
]);

export function filterByIsExclusive({ cheapProduct }: SerpProduct, filtersModel: FiltersModel): boolean {
  if (!filtersModel.isExclusive) return true;

  return cheapProduct !== null && isTravelata(cheapProduct);
}

export function filterByBudget({ cheapProduct }: SerpProduct, filtersModel: FiltersModel): boolean {
  const { min, max } = filtersModel.budget;

  if (!min && !max) return true;
  if (cheapProduct === null) return false;

  const { price } = cheapProduct;

  return (!min || price >= min) && (!max || price <= max);
}

export function filterByBonusMiles({ products }: SerpProduct, filtersModel: FiltersModel): boolean {
  if (!filtersModel.bonusMiles) return true;

  return products.some((product) => isTravelata(product));
}

export function filterByPrepayments({ products }: SerpProduct, filtersModel: FiltersModel): boolean {
  if (!filtersModel.prepayments.length) return true;

  return products.some((product) => {
    const keys = getProductPrepaymentsFilterKeys(product);

    return filtersModel.prepayments.some((key) => keys.includes(key));
  });
}

export function filterByOperators({ products }: SerpProduct, filtersModel: FiltersModel): boolean {
  if (!filtersModel.operators.length) return true;

  return products.some(({ operator }) => operator !== null && filtersModel.operators.includes(String(operator.id)));
}

export function filterByBeachLine(hotel: HotelProduct, filtersModel: FiltersModel): boolean {
  if (!filtersModel.beachLine.length) return true;

  const { beachLineType } = hotel;

  return beachLineType !== null && filtersModel.beachLine.includes(beachLineType);
}

export function filterByBeachType(hotel: HotelProduct, filtersModel: FiltersModel): boolean {
  if (!filtersModel.beachType.length) return true;

  const { beachType } = hotel;

  return beachType !== null && filtersModel.beachType.includes(beachType);
}

export function filterByBeachOwner(hotel: HotelProduct, filtersModel: FiltersModel): boolean {
  if (!filtersModel.beachOwner) return true;

  return hotel.beachOwner;
}

export function filterByNearTheCenter(hotel: HotelProduct, filtersModel: FiltersModel): boolean {
  if (!filtersModel.nearTheCenter) return true;

  return hotel.features.includes(HotelFeature.NearTheCenter);
}

export function filterByRating(hotel: HotelProduct, filtersModel: FiltersModel): boolean {
  if (filtersModel.rating === null) return true;
  if (hotel.rating === null) return false;

  const rating = Math.round(hotel.rating * RATING_PRECISION) / RATING_PRECISION;

  return rating >= RATING_FILTER_THRESHOLDS[filtersModel.rating];
}

export function filterByFacilities(hotel: HotelProduct, filtersModel: FiltersModel): boolean {
  if (!filtersModel.facilities.length) return true;

  return filtersModel.facilities.every((key) => hotel.facilities.includes(key));
}

export function filterByWaterActivities(hotel: HotelProduct, filtersModel: FiltersModel): boolean {
  if (!filtersModel.waterActivities.length) return true;

  return filtersModel.waterActivities.every((key) => hotel.waterActivities.includes(key));
}

export function filterByChildServices(hotel: HotelProduct, filtersModel: FiltersModel): boolean {
  if (!filtersModel.childServices.length) return true;

  return filtersModel.childServices.every((key) => {
    if (key === ChildService.SeaEntry) return hotel.hasConvenientSeaEntry;

    return hotel.childServices.includes(key);
  });
}

export function filterByRenovation(hotel: HotelProduct, filtersModel: FiltersModel): boolean {
  if (!filtersModel.renovation.length) return true;

  return filtersModel.renovation.some((key) => hotel.renovationTypes.includes(key));
}

export function filterByDistanceLift(hotel: HotelProduct, filtersModel: FiltersModel): boolean {
  if (!filtersModel.distanceLift.length) return true;

  const { distanceLift } = hotel;

  return distanceLift !== null && filtersModel.distanceLift.includes(distanceLift);
}

export function filterByRestType(hotel: HotelProduct, filtersModel: FiltersModel): boolean {
  if (filtersModel.restType === null) return true;

  return hotel.restTypes.includes(filtersModel.restType);
}

export function filterByProducts(products: readonly SerpProduct[], filtersModel: FiltersModel): SerpProduct[] {
  return products.filter((serpProduct) => isProductMatched(serpProduct, filtersModel));
}

export function filterByHotels(products: readonly SerpProduct[], filtersModel: FiltersModel): SerpProduct[] {
  return products.filter(({ hotel }) => isHotelMatched(hotel, filtersModel));
}

export function filterProducts(products: readonly SerpProduct[], filtersModel: FiltersModel): SerpProduct[] {
  return filterByProducts(filterByHotels(products, filtersModel), filtersModel);
}

export function hasFilteredProducts(products: readonly SerpProduct[], filtersModel: FiltersModel): boolean {
  return products.some(
    (serpProduct) => isHotelMatched(serpProduct.hotel, filtersModel) && isProductMatched(serpProduct, filtersModel),
  );
}

function isProductMatched(serpProduct: SerpProduct, filtersModel: FiltersModel): boolean {
  return (
    filterByIsExclusive(serpProduct, filtersModel) &&
    filterByBudget(serpProduct, filtersModel) &&
    filterByBonusMiles(serpProduct, filtersModel) &&
    filterByPrepayments(serpProduct, filtersModel) &&
    filterByOperators(serpProduct, filtersModel)
  );
}

function isHotelMatched(hotel: HotelProduct, filtersModel: FiltersModel): boolean {
  return (
    filterByBeachLine(hotel, filtersModel) &&
    filterByBeachType(hotel, filtersModel) &&
    filterByBeachOwner(hotel, filtersModel) &&
    filterByNearTheCenter(hotel, filtersModel) &&
    filterByRating(hotel, filtersModel) &&
    filterByFacilities(hotel, filtersModel) &&
    filterByWaterActivities(hotel, filtersModel) &&
    filterByChildServices(hotel, filtersModel) &&
    filterByRenovation(hotel, filtersModel) &&
    filterByDistanceLift(hotel, filtersModel) &&
    filterByRestType(hotel, filtersModel)
  );
}

function isTravelata(product: Product): boolean {
  return product.operator !== null && isTravelataOperatorId(product.operator.id);
}
